import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Wallet, 
  DollarSign, 
  Megaphone,
  Loader2,
  Save,
  Calendar
} from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';

const ExpensesTab = ({ tenantId, selectedPeriod, onUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expensesHistory, setExpensesHistory] = useState([]);
  const [formData, setFormData] = useState({
    operating_expenses: '',
    ad_spend: '',
    notes: ''
  });

  // Fetch expenses for selected period
  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/v1/profitability/expenses?year_month=${selectedPeriod}`, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      const data = response.data || response;
      setFormData({
        operating_expenses: data.operating_expenses?.toString() || '',
        ad_spend: data.ad_spend?.toString() || '',
        notes: data.notes || ''
      });
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch expenses history
  const fetchHistory = async () => {
    try {
      const response = await api.get('/api/v1/profitability/expenses/history', {
        headers: { 'X-Tenant-ID': tenantId }
      });
      setExpensesHistory((response.data || response).expenses || []);
    } catch (error) {
      console.error('Error fetching expenses history:', error);
    }
  };

  useEffect(() => {
    fetchExpenses();
    fetchHistory();
  }, [selectedPeriod]);

  // Save expenses
  const handleSave = async () => {
    try {
      setSaving(true);
      await api.post('/api/v1/profitability/expenses', {
        year_month: selectedPeriod,
        operating_expenses: parseFloat(formData.operating_expenses) || 0,
        ad_spend: parseFloat(formData.ad_spend) || 0,
        notes: formData.notes
      }, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      
      toast.success('Expenses saved');
      fetchHistory();
      if (onUpdate) onUpdate();
      
    } catch (error) {
      console.error('Error saving expenses:', error);
      toast.error('Failed to save expenses');
    } finally {
      setSaving(false);
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  // Format period display
  const formatPeriod = (yearMonth) => {
    const [year, month] = yearMonth.split('-');
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Calculate totals
  const totalOpex = parseFloat(formData.operating_expenses) || 0;
  const totalAdSpend = parseFloat(formData.ad_spend) || 0;
  const totalExpenses = totalOpex + totalAdSpend;

  return (
    <div className="space-y-3">
      {/* Compact Header */}
      <div>
        <h3 className="text-base font-semibold text-white">Monthly Expenses</h3>
        <p className="text-xs text-gray-400">Track operating expenses and ad spend</p>
      </div>

      {/* Compact Current Period Expenses */}
      <div className="bg-[#1a2744] border border-gray-700/50 rounded-lg p-3">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-sm font-medium text-white">{formatPeriod(selectedPeriod)}</span>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 mb-3">
              {/* Operating Expenses */}
              <div>
                <label className="flex items-center gap-1.5 text-xs text-gray-400 mb-1.5">
                  <Wallet className="w-3 h-3" />
                  Operating Expenses
                </label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                  <Input
                    type="number"
                    value={formData.operating_expenses}
                    onChange={(e) => setFormData({ ...formData, operating_expenses: e.target.value })}
                    className="pl-6 bg-[#0f1729] border-gray-600 text-white text-sm h-9"
                    placeholder="0"
                  />
                </div>
                <p className="text-[10px] text-gray-500 mt-0.5">Rent, utilities, salaries, etc.</p>
              </div>

              {/* Ad Spend */}
              <div>
                <label className="flex items-center gap-1.5 text-xs text-gray-400 mb-1.5">
                  <Megaphone className="w-3 h-3" />
                  Advertising Spend
                </label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                  <Input
                    type="number"
                    value={formData.ad_spend}
                    onChange={(e) => setFormData({ ...formData, ad_spend: e.target.value })}
                    className="pl-6 bg-[#0f1729] border-gray-600 text-white text-sm h-9"
                    placeholder="0"
                  />
                </div>
                <p className="text-[10px] text-gray-500 mt-0.5">Amazon PPC, Google Ads, etc.</p>
              </div>
            </div>

            {/* Notes - Compact */}
            <div className="mb-3">
              <label className="block text-xs text-gray-400 mb-1">Notes (optional)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full bg-[#0f1729] border border-gray-600 rounded-lg p-2 text-white text-xs resize-none h-12"
                placeholder="Notes..."
              />
            </div>

            {/* Compact Summary */}
            <div className="bg-[#0f1729] rounded-lg p-2.5 mb-3">
              <div className="flex justify-between py-0.5 text-xs">
                <span className="text-gray-400">Operating Expenses</span>
                <span className="font-mono text-white">{formatCurrency(totalOpex)}</span>
              </div>
              <div className="flex justify-between py-0.5 text-xs">
                <span className="text-gray-400">Ad Spend</span>
                <span className="font-mono text-white">{formatCurrency(totalAdSpend)}</span>
              </div>
              <div className="flex justify-between py-1 text-sm font-medium border-t border-gray-700 mt-1 pt-1">
                <span className="text-white">Total Fixed Costs</span>
                <span className="font-mono text-amber-400">{formatCurrency(totalExpenses)}</span>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs px-3"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                Save Expenses
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Compact Expenses History */}
      <div className="bg-[#1a2744] border border-gray-700/50 rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-700/50">
          <h4 className="text-xs font-medium text-white">Expenses History</h4>
        </div>
        {expensesHistory.length === 0 ? (
          <div className="text-center py-6 text-gray-400">
            <Wallet className="w-6 h-6 mx-auto mb-1.5 opacity-50" />
            <p className="text-xs">No expense records yet</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-[#0f1729]">
              <tr>
                <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase">Period</th>
                <th className="px-3 py-1.5 text-right text-[10px] font-medium text-gray-400 uppercase">OpEx</th>
                <th className="px-3 py-1.5 text-right text-[10px] font-medium text-gray-400 uppercase">Ad Spend</th>
                <th className="px-3 py-1.5 text-right text-[10px] font-medium text-gray-400 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {expensesHistory.slice(0, 6).map((exp) => (
                <tr 
                  key={exp._id || exp.year_month} 
                  className={`hover:bg-gray-800/30 ${exp.year_month === selectedPeriod ? 'bg-emerald-500/10' : ''}`}
                >
                  <td className="px-3 py-1.5 text-xs text-white">{formatPeriod(exp.year_month)}</td>
                  <td className="px-3 py-1.5 text-xs text-right font-mono text-white">{formatCurrency(exp.operating_expenses)}</td>
                  <td className="px-3 py-1.5 text-xs text-right font-mono text-white">{formatCurrency(exp.ad_spend)}</td>
                  <td className="px-3 py-1.5 text-xs text-right font-mono text-amber-400">
                    {formatCurrency((exp.operating_expenses || 0) + (exp.ad_spend || 0))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ExpensesTab;
